import React from 'react';
import { Button } from '@shared/ui/button';
import { OrderSummary, SafeAndSecure } from './SharedCards';
import { Smartphone, Lock } from 'lucide-react';
import { useConnectPaymentMethod, usePSPConfig, usePayBill } from "@features/billingPayment/hooks";
import { getLoginDataFromStorage } from "@shared/utils/loginUtils";
import { useToast } from "@shared/hooks/use-toast";

import { useSearchParams } from 'react-router-dom';

export const ReviewPayStep = ({ onNext, onPrevious, allStepData }: any) => {
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type') || 'bills';

  const selectedBillIds = allStepData?.[0]?.selectedBills || [];
  const selectedBillsData = allStepData?.[0]?.selectedBillsData || [];
  const subtotal = selectedBillsData.reduce((sum: any, bill: any) => sum + bill.amount, 0);
  const grandTotal = subtotal ;

  const { remoteUtilityId } = getLoginDataFromStorage();
  const consumerDetails = localStorage.getItem("loginResult");
  const parsed = consumerDetails ? JSON.parse(consumerDetails) : null;
  
  const { data: pspConfig } = usePSPConfig(remoteUtilityId);
  console.log('pspConfig',pspConfig)
  const activePsp = pspConfig?.find((item: any) => item.isActive && item.organizationName?.toLowerCase() === 'stripe');
  // const activePsp = pspConfig[1];
    console.log('pspConfig',activePsp)

  const activePspId = activePsp?.pspId;
  const activePspName = activePsp?.organizationName;

  const { mutate: connectPaymentMethod, isPending: isConnecting } = useConnectPaymentMethod();
  const { mutate: payBill, isPending: isPayingBill } = usePayBill();
  const isProcessing = isConnecting || isPayingBill;
  const { toast } = useToast();
  
  const baseUrl = import.meta.env.VITE_PAYMENT_API_ENDPOINT;

  const handlePayment = async () => {
    if (type === 'services') {
      const today = new Date().toISOString().split('T')[0];
      const servicesPayload = {
        remote_utility_id: Number(remoteUtilityId),
        consumer: parsed?.result?.consumerId,
        payment_mode: "Cash#9",
        amount: grandTotal,
        cheque_date: today,
        payment_date: today,
        payment_pay_type: "5",
        payment_channel: "PhonePay#6",
        source: 1,
        payment_received_status: 1,
        status: "CREDIT",
        create_credit_note: false,
        remote_bill_id: 0,
        extra_data: {
            excess_refund: 0
        },
        service_requests: selectedBillsData.map((srv: any) => ({
            consumer_support_request: srv.id,
            amount: srv.amount
        }))
      };

      payBill(servicesPayload as any, {
        onSuccess: () => {
          onNext(); // Advance to ConfirmationStep
        },
        onError: (error: any) => {
          toast({
            title: "Error",
            description: error.message || "Payment failed.",
            variant: "destructive",
          });
        }
      });
      return;
    }

    if (!activePspId) {
      toast({
        title: "Error",
        description: "Payment Method is not set from the utility yet.",
        variant: "destructive",
      });
      return;
    }

    const primaryBillId = selectedBillsData[0]?.id;
    
    const basePayload = {
      amount: grandTotal,
      remote_consumer_id: parsed?.result?.consumerId,
      psp_mapping_id: activePspId,
      remote_utility_id: Number(remoteUtilityId),
      description: "It is a billing payment",
      payment_type: "BILLING",
      remote_reference_entity_id: primaryBillId,
      source: 1,
    };

    let payload: Record<string, any> = {};
    if (!activePspName || !activePspId) {
      toast({
        title: "Error",
        description: "Cannot find any active payment method.",
        variant: "destructive",
      });
      return;
    }

    switch (activePspName.toLowerCase()) {
      case "stripe":
        payload = {
          ...basePayload,
          redirect_url: `${baseUrl}dashboard/billing/pay?status=success`,
          cancel_url: `${baseUrl}dashboard/billing/pay?status=failed`,
          payment_method: "Card",
        };
        break;

      case "doku":
        payload = {
          ...basePayload,
          success_url: `${baseUrl}doku/redirect/`,
          cancel_url: `${baseUrl}doku/redirect/`,
          invoice_number: `INV-${Date.now()}-${parsed?.result?.user?.id}`,
          name: `${parsed?.result?.firstName} ${parsed?.result?.lastName}`,
          email: parsed?.result?.user?.email?.trim(),
          phone: parsed?.result?.mobileNo,
        };
        break;

      default:
        toast({
          title: "Unsupported PSP",
          description: `Payment provider "${activePspName}" is not supported.`,
          variant: "destructive",
        });
        return;
    }

    connectPaymentMethod(payload, {
      onSuccess: (response: any) => {
        if (response?.formData && response?.url) {
          const form = document.createElement("form");
          form.method = "POST";
          form.action = response.url;
          form.target = "_blank";
          form.style.display = "none";

          Object.entries(response.formData).forEach(([key, value]) => {
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = key; 
            input.value = value as string;
            form.appendChild(input);
          });

          document.body.appendChild(form);
          form.submit();
          
          onNext(); // Advance to ConfirmationStep
          return;
        } else if (response?.url) {
          window.open(response.url, "_blank", "noopener,noreferrer");
          onNext();
          return;
        }
        onNext();
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      },
    });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left Pane */}
      <div className="lg:col-span-2">
        <div className="bg-white rounded-xl border p-6 h-full flex flex-col">
          <h3 className="text-gray-900 font-semibold text-lg mb-6">Review & Confirm Payment</h3>

          <div className="text-xs font-bold text-gray-500 tracking-wider mb-3">
            {type === 'services' ? 'Services to be Paid' : type === 'installment' ? 'Installment to be Paid' : 'Bills to be Paid'}
          </div>
          
          <div className="space-y-3 mb-8">
            {selectedBillsData.length > 0 ? (
              selectedBillsData.map(bill => (
                <div key={bill.id} className="flex justify-between items-center bg-gray-50/50 rounded-lg p-4 border border-gray-100">
                  <div>
                    <div className="font-semibold text-gray-900 text-sm mb-0.5">{bill.month}</div>
                    <div className="text-xs text-gray-400">{bill.id}</div>
                  </div>
                  <div className="font-bold text-gray-900">${bill.amount.toFixed(2)}</div>
                </div>
              ))
            ) : (
              <div className="text-sm text-gray-500 italic p-4">
                No {type === 'services' ? 'services' : type === 'installment' ? 'installment' : 'bills'} selected
              </div>
            )}
          </div>

          <div className="space-y-3 mb-6">
            <div className="flex justify-between text-sm text-gray-500">
              <span>Subtotal</span>
              <span className="text-gray-900 font-medium">${subtotal.toFixed(2)}</span>
            </div>
            {/* <div className="flex justify-between text-sm text-gray-500">
              <span>Late Payment Fees</span>
              <span className="text-red-600 font-medium">${lateFees.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm text-gray-500">
              <span>Taxes & Levies</span>
              <span className="text-gray-900 font-medium">${taxes.toFixed(2)}</span>
            </div> */}
          </div>

          <div className="flex justify-between items-center border-t border-b border-gray-100 py-4 mb-6">
            <span className="font-bold text-gray-900">Grand Total</span>
            <span className="font-bold text-blue-700 text-xl">${grandTotal.toFixed(2)}</span>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 flex items-center gap-3 mb-6 border border-gray-100">
            <div className="w-10 h-10 bg-white rounded-md border flex items-center justify-center shrink-0">
              <Smartphone className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-0.5">Paying via</div>
              <div className="font-semibold text-gray-900 text-sm">Online Payment</div>
            </div>
          </div>

          <div className="flex items-start gap-3 mb-6">
            <input type="checkbox" className="mt-1 border-gray-300 rounded text-blue-600 w-4 h-4 cursor-pointer" />
            <p className="text-sm text-gray-600">
              I agree to the <a className="text-blue-600 hover:underline">Terms of Service</a> and confirm the payment details above are correct. I understand that this payment is non-refundable once processed.
            </p>
          </div>

          <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-6 flex-1">
            <Lock className="w-3 h-3 text-green-600" />
            <span>256-bit TLS encryption • PCI DSS Level 1 • Powered by Stripe</span>
          </div>

          <div className="flex items-center justify-between">
            <Button onClick={onPrevious} variant="outline" className="border-gray-300 text-gray-700 disabled:opacity-50" disabled={isProcessing}>
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path></svg> Back
            </Button>
            <Button onClick={handlePayment} disabled={grandTotal === 0 || isProcessing} className="bg-blue-400 hover:bg-blue-500 text-white rounded-md px-6 flex items-center gap-2 font-medium disabled:opacity-50">
              <Lock className="w-4 h-4" /> {isProcessing ? 'Processing...' : `Pay $${grandTotal.toFixed(2)}`}
            </Button>
          </div>
        </div>
      </div>

      {/* Right Pane */}
      <div className="lg:col-span-1">
        <OrderSummary allStepData={allStepData} />
        <SafeAndSecure />
      </div>
    </div>
  );
};
